import type { RefObject } from 'react';

import { type Table } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';

import type { CharlieTableProps } from '../../charlie-table.types';

export const useCharlieRowVirtualization = <T extends object>({
	enableRowVirtualization,
	table,
	tableContainerRef,
	rowSpacing,
	rowVirtualizerRef,
	rowVirtualizerOptions,
}: {
	enableRowVirtualization: boolean;
	table: Table<T>;
	tableContainerRef: RefObject<HTMLDivElement>;
	rowSpacing?: CharlieTableProps<T>['rowSpacing'];
	rowVirtualizerRef?: NonNullable<CharlieTableProps<T>['refs']>['rowVirtualizerRef'];
	rowVirtualizerOptions?: CharlieTableProps<T>['rowVirtualizerOptions'];
}) => {
	/**
	 * This theoretically breaks the rule of hooks but the prop can't be changed after the table is rendered
	 * so in practice it's fine
	 */

	if (!enableRowVirtualization) {
		return undefined;
	}

	const customRowVirtualizerOptions =
		typeof rowVirtualizerOptions === 'function'
			? rowVirtualizerOptions({ table })
			: rowVirtualizerOptions;

	const { rows } = table.getRowModel();
	const rowHeight = rowSpacing === 'compact' ? 38 : 46;

	// eslint-disable-next-line react-hooks/rules-of-hooks
	const rowVirtualizer = useVirtualizer({
		count: rows.length,
		getScrollElement: () => tableContainerRef?.current,
		//measure dynamic row height, except in firefox because it measures table border height incorrectly
		measureElement:
			typeof window !== 'undefined' && navigator.userAgent.indexOf('Firefox') === -1
				? (element) => element?.getBoundingClientRect().height
				: undefined,
		estimateSize: () => rowHeight,
		overscan: 10,
		...customRowVirtualizerOptions,
	});
	if (rowVirtualizerRef) {
		rowVirtualizerRef.current = rowVirtualizer;
	}
	return rowVirtualizer;
};
